import { getDisplayToken } from '@/domain/hooks/useDisplayToken'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { SuccessViewCheckmark } from '@/features/dialogs/common/components/success-view/SuccessViewCheckmark'
import { SuccessViewContent } from '@/features/dialogs/common/components/success-view/SuccessViewContent'
import { SuccessViewProceedButton } from '@/features/dialogs/common/components/success-view/SuccessViewProceedButton'
import { SuccessViewSummaryPanel } from '@/features/dialogs/common/components/success-view/SuccessViewSummaryPanel'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { Typography } from '@/ui/atoms/typography/Typography'
import { useChainId } from 'wagmi'

export interface AssetSwapSuccessViewProps {
  fromToken: Token
  fromAmount: NormalizedUnitNumber
  toToken: Token
  toAmount: NormalizedUnitNumber
  fromCuratorName?: string
  fromMarket?: { name?: string }
  toCuratorName?: string
  toMarket?: { name?: string }
  onProceed: () => void
  proceedText?: string
  isWalletDestination?: boolean
  isSameUnderlying?: boolean
}

export function AssetSwapSuccessView({
  fromToken,
  fromAmount,
  toToken,
  toAmount,
  fromCuratorName,
  fromMarket,
  toCuratorName,
  toMarket,
  onProceed,
  proceedText = 'View in dashboard',
  isWalletDestination,
  isSameUnderlying,
}: AssetSwapSuccessViewProps) {
  const chainId = useChainId()
  const displayFromToken = getDisplayToken(chainId, fromToken)
  const displayToToken = getDisplayToken(chainId, toToken)

  const fromMarketInfo = [fromCuratorName && `by ${fromCuratorName}`, fromMarket?.name].filter(Boolean).join(' • ')
  const toMarketInfo = [toCuratorName && `by ${toCuratorName}`, toMarket?.name].filter(Boolean).join(' • ')

  // Use "Moved" for same underlying (1:1 transfer), "Swapped" for different underlying
  const getTitle = () => {
    if (isWalletDestination) return 'Swapped to Wallet'
    if (isSameUnderlying) return 'Moved'
    return 'Swapped'
  }

  return (
    <SuccessViewContent>
      <SuccessViewCheckmark isModal />
      <SuccessViewSummaryPanel title={getTitle()}>
        {/* From Token */}
        <div className="flex items-center gap-2">
          <TokenIcon token={fromToken} className="h-6" />
          <div className="flex flex-col">
            <span className="text-muted-foreground text-sm">{displayFromToken.symbol}</span>
            {fromMarketInfo && <span className="text-muted-foreground text-xs">{fromMarketInfo}</span>}
          </div>
        </div>
        <div className="flex grow flex-col">
          <Typography className="text-right font-primary">{fromToken.format(fromAmount, { style: 'auto' })}</Typography>
        </div>
      </SuccessViewSummaryPanel>

      {/* Arrow */}
      <div className="flex justify-center py-1">
        <svg className="h-4 w-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </div>

      <SuccessViewSummaryPanel title="">
        {/* To Token */}
        <div className="flex items-center gap-2">
          <TokenIcon token={toToken} className="h-6" />
          <div className="flex flex-col">
            <span className="font-semibold">{displayToToken.symbol}</span>
            {toMarketInfo && !isWalletDestination && (
              <span className="text-muted-foreground text-xs">{toMarketInfo}</span>
            )}
          </div>
        </div>
        <div className="flex grow flex-col">
          <Typography className="text-right font-primary">{toToken.format(toAmount, { style: 'auto' })}</Typography>
          <Typography variant="prompt" className="text-right">
            {toToken.formatUSD(toAmount)}
          </Typography>
        </div>
      </SuccessViewSummaryPanel>
      <SuccessViewProceedButton onProceed={onProceed}>{proceedText}</SuccessViewProceedButton>
    </SuccessViewContent>
  )
}
